import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  createRfidCard, updateRfidCard, fetchRfidRegistrationFee, getDisplayErrorMessage,
  type RfidCardRow, type RfidRegistrationFee,
} from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { UserTypeahead } from '@/components/ui/UserTypeahead';
import formStyles from '@/components/ui/Form.module.css';
import styles from './RfidCardFormModal.module.css';
import { Switch } from '@/components/ui/Switch';

/**
 * Create / edit form for an RfidCard.
 *
 * Create: tag_uid (required), label (optional), driver (typeahead,
 *   required). Tenant is auto-attached from the caller's admin
 *   scope server-side — no tenant picker here (spec: hide when the
 *   admin has single-tenant access, which is the current state).
 * Edit: tag_uid is read-only (hardware UIDs don't change). Label,
 *   driver, and is_active are editable.
 */

interface Props {
  mode: 'create' | 'edit';
  card?: RfidCardRow;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

export function RfidCardFormModal({ mode, card, onClose, onSaved }: Props) {
  const [tagUid, setTagUid] = useState(card?.tag_uid ?? '');
  const [label, setLabel] = useState(card?.label ?? '');
  // Driver selection now flows through the shared UserTypeahead in
  // single-select mode (maxSelections=1). The component returns an
  // array; this wrapper unwraps to a single id for the rest of the
  // form's logic. Initial value preserves the card's existing user
  // when editing.
  const [driverIds, setDriverIds] = useState<string[]>(
    card?.user?._id ? [card.user._id] : [],
  );
  const driverId = driverIds[0] ?? null;
  const [isActive, setIsActive] = useState(card?.is_active ?? true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Fee disclosure — what THIS registration will charge, resolved
  // server-side from the caller's channel + tenant pricing. Create
  // mode only; edits never charge. FAIL CLOSED: until the fee has
  // loaded, Register is disabled — a swallowed fetch error must not
  // reopen the undisclosed-charge path this modal exists to close.
  const [fee, setFee] = useState<RfidRegistrationFee | null>(null);
  const [feeState, setFeeState] = useState<'loading' | 'ok' | 'error'>('loading');
  useEffect(() => {
    if (mode !== 'create') { setFeeState('ok'); return; }
    setFeeState('loading');
    fetchRfidRegistrationFee()
      .then((f) => { setFee(f); setFeeState('ok'); })
      .catch(() => { setFee(null); setFeeState('error'); });
  }, [mode]);

  const handleSubmit = async (): Promise<void> => {
    setSubmitting(true);
    setError('');
    try {
      if (mode === 'create') {
        if (!tagUid.trim()) throw new Error('Tag UID is required');
        if (!driverId) throw new Error('Driver is required');
        await createRfidCard({
          tag_uid: tagUid.trim(),
          user_id: driverId,
          ...(label.trim() ? { label: label.trim() } : {}),
        });
      } else if (card) {
        const patch: Record<string, unknown> = {};
        if ((card.label ?? '') !== label.trim()) {
          patch['label'] = label.trim() || null;
        }
        if (driverId && driverId !== card.user?._id) {
          patch['user_id'] = driverId;
        }
        if (isActive !== card.is_active) {
          patch['is_active'] = isActive;
        }
        if (Object.keys(patch).length > 0) {
          await updateRfidCard(card._id, patch);
        }
      }
      await onSaved();
    } catch (err) {
      // Shared body-parsing helper so the server's 402/409 copy (org
      // card declined, tag already registered…) reaches the admin
      // instead of a generic status line.
      setError(await getDisplayErrorMessage(err, 'Request failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={mode === 'create' ? 'Add RFID card' : 'Edit RFID card'}
      size="md"
    >
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        {error && <div className={styles.error}>{error}</div>}

        <div className={formStyles.field}>
          <label className={`${formStyles.label} ${mode === 'create' ? formStyles.required : ''}`}>
            Tag UID
          </label>
          <input
            className={formStyles.input}
            value={tagUid}
            onChange={(e) => setTagUid(e.target.value)}
            disabled={mode === 'edit'}
            placeholder="e.g. 2002610, 4CA7BCEF"
            required
          />
          <p className={formStyles.hint}>
            {mode === 'create'
              ? 'The hex UID the charger reports. Get this from the Logs tab\'s Authorize entry or tap the card once.'
              : 'Hardware UIDs don\'t change. Reassign to a different driver if the card changed hands.'}
          </p>
        </div>

        <div className={formStyles.field}>
          <label className={formStyles.label}>Label</label>
          <input
            className={formStyles.input}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Honda key fob, Wallet card…"
          />
        </div>

        <div className={formStyles.field}>
          <label className={`${formStyles.label} ${mode === 'create' ? formStyles.required : ''}`}>
            Driver
          </label>
          <UserTypeahead
            selectedUserIds={driverIds}
            onChange={setDriverIds}
            maxSelections={1}
            disabled={submitting}
            placeholder="Search email, username, or name…"
            ariaLabel="Driver search"
          />
        </div>

        {mode === 'create' && (
          <p className={formStyles.hint}>
            {feeState === 'loading'
              ? 'Loading the registration fee…'
              : feeState === 'error'
                ? 'Could not load the registration fee — registering is disabled. Close and reopen to retry.'
                : fee && fee.charges && fee.payer === 'operator'
                  ? `Your organization will be charged $${(fee.amount_cents / 100).toFixed(2)} when you register this card.`
                  : fee && fee.charges && fee.payer === 'driver'
                    ? `The driver's saved card will be charged $${(fee.amount_cents / 100).toFixed(2)} when you register this card.`
                    : 'No registration fee applies for your channel.'}
          </p>
        )}

        {mode === 'edit' && (
          <div className={formStyles.field}>
            <label className={formStyles.label}>Status</label>
            <Switch
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              label="Card is active"
            />
            <p className={formStyles.hint}>
              Disabling blocks new taps. Session history stays intact.
            </p>
          </div>
        )}

        <div className={styles.actions}>
          <button
            type="button"
            className={formStyles.btnSecondary}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={formStyles.btnPrimary}
            disabled={submitting || (mode === 'create' && feeState !== 'ok')}
          >
            {submitting
              ? <><Loader2 size={12} className={styles.spin} /> Saving…</>
              : mode === 'create' ? 'Register card' : 'Save changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Driver picker was extracted to apps/admin/src/components/ui/UserTypeahead.tsx
// (commit 10 of third-party-charger-access ticket). The shared
// component now serves both this RFID flow and the guest pass list
// on ChargerDetailPage.
